import React from 'react';
import { Transition } from 'react-transition-group';

type Direction = 'up' | 'down' | 'left' | 'right' | 'no';

const transitionDirectionIn: Record<Direction, {entering: React.CSSProperties; entered: React.CSSProperties} > = {
  up: {
    entering: { opacity: 0, transform: 'translateY(8px)' },
    entered: { opacity: 1, transform: 'translateY(0px)' },
  },
  down: {
    entering: { opacity: 0, transform: 'translateY(-8px)' },
    entered: { opacity: 1, transform: 'translateY(0px)' },
  },
  left: {
    entering: { opacity: 0, transform: 'translateX(8px)' },
    entered: { opacity: 1, transform: 'translateX(0px)' },
  },
  right: {
    entering: { opacity: 0, transform: 'translateX(-8px)' },
    entered: { opacity: 1, transform: 'translateX(0px)' },
  },
  no: {
    entering: { opacity: 0 },
    entered: { opacity: 1 },
  }
};

const transitionDirectionOut: Record<Direction, {exiting: React.CSSProperties; exited: React.CSSProperties} > = {
  up: {
    exiting: { opacity: 0, transform: 'translateY(-8px)' },
    exited: { opacity: 0, transform: 'translateY(-8px)' },
  },
  down: {
    exiting: { opacity: 0, transform: 'translateY(8px)' },
    exited: { opacity: 0, transform: 'translateY(8px)' },
  },
  left: {
    exiting: { opacity: 0, transform: 'translateX(-8px)' },
    exited: { opacity: 0, transform: 'translateX(-8px)' },
  },
  right: {
    exiting: { opacity: 0, transform: 'translateX(8px)' },
    exited: { opacity: 0, transform: 'translateX(8px)' },
  },
  no: {
    exiting: { opacity: 0 },
    exited: { opacity: 0 },
  },
};


export type FadeAndSlideTransitionDirection = `${Direction}-${Direction}`;


export type FadeAndSlideProps = {
  in: boolean;
  timeout?: number;
  children: React.ReactElement<{ style: React.CSSProperties }>;
  onExited?: () => void;
  direction?: FadeAndSlideTransitionDirection;
};


export const FadeAndSlide: React.VFC<FadeAndSlideProps> = ({ in: inProp, timeout = 300, children, onExited, direction = 'left-right' }) => {
  const defaultStyle: React.CSSProperties = React.useMemo(() => ({
    transitionDuration: `${timeout}ms`,
  }), [ timeout ]);

  const transitionStyles = React.useMemo(() => {
    const [ inKey, outKey ] = direction.split('-') as [Direction, Direction];

    return {
      ...transitionDirectionIn[inKey],
      ...transitionDirectionOut[outKey],
      unmounted: null,
    };
  }, [ direction ]);

  return (
    <Transition in={inProp} timeout={timeout} onExited={onExited} appear>
      {(state) =>
        React.cloneElement(children, {
          ...children.props,
          style: {
            ...children.props.style,
            ...defaultStyle,
            ...transitionStyles[state]
          }
        })
      }
    </Transition>
  );
};
